import React, { useEffect, useState, useContext } from 'react'
import { Container, Col, Row } from 'react-bootstrap'
import styled from 'styled-components'
import { TbDownload } from 'react-icons/tb'
import Loader from 'react-loader-spinner'
import { useSearch } from 'hooks/useSearch'
import { useEnum } from 'hooks/useEnum'
import { I18nContext } from 'i18n'
import {
  ProgramList,
  SearchPanel,
  Permission,
  ServerPagination,
  Title,
  BackdropLoader,
  ClickableIcon,
  InfoTooltipLink,
} from 'components'
import theme from '../../themes/default'
import { useProgramContext } from 'providers/programProvider'
import { omit } from 'lodash'
import { useCountryCodes } from 'hooks/useCountryCodes'
import { useStateCodes } from 'hooks/useStateCodes'
import { useExportScholarships } from 'hooks/useExportScholarships'
import ScholarshipSources from '../../molecules/ScholarshipSources/ScholarshipSources'
import SearchSelector from './SearchSelector'
import OrganizationList from './OrganizationList'
import { useQueryParams } from 'raviger'

const ScholarshipSearch = ({ search }) => {
  const [queryParams] = useQueryParams()
  const { org, isNspaMember, orgState } = queryParams
  const { translate } = useContext(I18nContext)
  const {
    dataPrograms,
    loadingPrograms,
    setFilter,
    executeOrganizations,
    executeCriteria,
    dataCriteria,
    clearFilters,
  } = useSearch(search)

  const { countryList } = useCountryCodes()
  const { stateList } = useStateCodes()

  const { schemas: enumSchemas, getEnum } = useEnum()
  const [criterias, setCriterias] = useState()
  const [state] = useProgramContext()

  const { exportToSpreadsheet } = useExportScholarships(
    dataPrograms?.programs,
    search
  )

  useEffect(() => {
    executeCriteria()
    executeOrganizations()
    getEnum()
  }, [])

  useEffect(() => {
    if (search) {
      handleFilter(
        {
          ...(org && { org: [org] }),
          ...(orgState && { orgStates: [orgState] }),
          ...(isNspaMember && { isNspaMember: isNspaMember }),
          ...state?.params,
          force: state?.params?.force ? !state?.params?.force : false,
        },
        true
      )
    }
  }, [search])

  useEffect(() => {
    if (enumSchemas && dataCriteria) {
      const conditions = enumSchemas?.conditionCriteria.map(
        ({ elementValue }) => elementValue
      )
      setCriterias({ ...dataCriteria, conditions })
    }
  }, [enumSchemas, dataCriteria])

  useEffect(() => {
    if (criterias) {
      handleFilter({ ...state?.params })
    }
  }, [criterias])

  const handleFilter = (filter, restartPage) => {
    const page = restartPage ? 1 : filter?.page || state?.params?.page
    setFilter({ ...filter, page })
  }

  return (
    <SearchContainer>
      <Permission
        permission={['scholarships']}
        message={translate('search_permission')}
      >
        <>
          <SearchSelector selected={search} />
          <Row>
            <Col md="3">
              <Legend className="d-flex mb-2">
                Search & Filter
                <InfoTooltipLink
                  text={translate('tooltipProgramSearchFilter')}
                  link={translate('programSearchFilterLink')}
                  className="ml-2"
                />
              </Legend>
              {criterias && enumSchemas && countryList && stateList ? (
                <SearchPanel
                  criteria={criterias}
                  onFilter={handleFilter}
                  onClear={clearFilters}
                  defaultValue={{
                    ...omit(state?.params, 'citizenshipStatuses'),
                    citizenshipEligibilities:
                      state?.params?.citizenshipStatuses,
                  }}
                  {...{ enumSchemas, stateList, countryList }}
                />
              ) : (
                <BackdropLoader />
              )}
            </Col>
            <Col>
              <div className="d-flex justify-content-between mb-2">
                <Legend>Results</Legend>
                {dataPrograms?.programs && (
                  <ClickableIcon
                    label="Download"
                    icon={<TbDownload />}
                    className="justify-content-end"
                    onClick={exportToSpreadsheet}
                    tooltip={
                      search==='organizations' ? 
                        "Download the organizations on this page as an Excel file" 
                        : 
                        "Download the scholarships on this page as an Excel file"
                    }
                    disabled={!dataPrograms?.programs?.length}
                  />
                )}
              </div>
              {loadingPrograms && (
                <Spinner>
                  <Loader
                    type="Oval"
                    height={30}
                    color={theme.palette.primary[0]}
                    timeout={10000}
                  />
                </Spinner>
              )}

              {!loadingPrograms && dataPrograms && (
                <>
                  {search === 'organizations' ? (
                    <OrganizationList
                      data={dataPrograms?.programs}
                      translate={translate}
                    />
                  ) : (
                    <ProgramList
                      programs={dataPrograms?.programs}
                      paginate={false}
                      fromSearch={true}
                    />
                  )}
                </>
              )}
              {dataPrograms?.programs.length == 0 && !loadingPrograms && (
                <span>{translate('search_no_records')}</span>
              )}
              {dataPrograms && (
                <ServerPagination
                  onChange={handleFilter}
                  {...dataPrograms?.meta}
                  $loading={loadingPrograms}
                  selectedPage={state?.params?.page}
                />
              )}
              <hr className="my-3 border-dark" />
              <ScholarshipSources showPublicTaxRecord={false} />
            </Col>
          </Row>
        </>
      </Permission>
    </SearchContainer>
  )
}

const Spinner = styled.div`
  text-align: center;
`

const SearchContainer = styled(Container)`
  margin: 0 auto;
`

const Legend = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.palette.grayscale[1]};
`

export default ScholarshipSearch
